import React, { useEffect, useState } from "react";
import { Text, View } from "react-native";
import { EStyleSheet, ExtendedStyleType, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { SubmitPicks, PlatformBetOfferType, PlatformEvent, PlatformOffer } from "@freeplay/shared-code";
import { GlobalStyles } from "../style/Style";
import { Prop } from "./Prop";
import { useServices } from "../../contexts/ServiceContext";
import { MediaQueries } from "../hooks/dynamicStyleHook";

export interface SelectionContainerProps {
    testID?: string;
    currentPick?: SubmitPicks;
    oddsType?: string;
    index: number;
    matchData: PlatformEvent;
    gameData: PlatformOffer;
    incomingStyle: ExtendedStyleType;
    disabled?: boolean;
    isLast?: boolean;
    onSelect: (picks: SubmitPicks, index: number) => void;
}

export function formatPositiveOddString(x: string): string {
    if (parseInt(x, 10) > 0) {
        return "+" + x;
    }

    return x;
}

export const SelectionContainer: React.FC<SelectionContainerProps> = ({
    matchData,
    gameData,
    incomingStyle,
    currentPick,
    index,
    disabled,
    isLast,
    onSelect,
}) => {
    const { edgeDeploy } = useServices();
    const styles = useDynamicStyles(GenerateSelectionContainerStyles);
    const [isAwaySelected, setIsAwaySelected] = useState<boolean>(false);
    const [isHomeSelected, setIsHomeSelected] = useState<boolean>(false);

    useEffect(() => {
        const getWhoIsSelected = (pick: SubmitPicks) => {
            if (
                pick.platformEventId === gameData.platformEvent &&
                pick.platformOfferId === gameData.platformOutcomes[1].platformOffer &&
                pick.platformOutcomeId === gameData.platformOutcomes[1].id
            ) {
                setIsAwaySelected(true);
                setIsHomeSelected(false);
            } else if (
                pick.platformEventId === gameData.platformEvent &&
                pick.platformOfferId === gameData.platformOutcomes[0].platformOffer &&
                pick.platformOutcomeId === gameData.platformOutcomes[0].id
            ) {
                setIsHomeSelected(true);
                setIsAwaySelected(false);
            }
        };

        if (currentPick) {
            getWhoIsSelected(currentPick);
        } else {
            setIsHomeSelected(false);
            setIsAwaySelected(false);
        }
    }, [currentPick, gameData.platformEvent, gameData.platformOutcomes]);

    const isOverUnder = gameData.betOfferType === PlatformBetOfferType.OVERUNDER;
    const isSpread = gameData.betOfferType === PlatformBetOfferType.HANDICAP;
    const homeName = isOverUnder ? gameData.platformOutcomes[0].label : matchData.homeName;
    const awayName = isOverUnder ? gameData.platformOutcomes[1].label : matchData.awayName;
    const homeOdds = formatPositiveOddString(gameData.platformOutcomes[0].oddsAmerican); //Home Seems to be 0 away seems to be 1 might need to make a function that can determine this using the two data sets of match & game
    const awayOdds = formatPositiveOddString(gameData.platformOutcomes[1].oddsAmerican);
    const homeSpread = formatPositiveOddString((gameData.platformOutcomes[0].line / 1000).toString(10));
    const awaySpread = formatPositiveOddString((gameData.platformOutcomes[1].line / 1000).toString(10));
    const overUnderOdd = (gameData.platformOutcomes[1].line / 1000).toString(10);
    const circleText = isOverUnder ? "OR" : "AT";

    let descriptor = "To Win";
    let homePropString = homeOdds;
    let awayPropString = awayOdds;

    if (isSpread) {
        descriptor = "With Handicap";
        homePropString = homeSpread;
        awayPropString = awaySpread;
    }
    if (isOverUnder) {
        descriptor = "Total Points";
        homePropString = overUnderOdd;
        awayPropString = overUnderOdd;
    }

    return (
        <View style={[styles.outerContainer, incomingStyle, isLast ? styles.noBottomMargin : {}]}>
            <Text style={[styles.descriptor, edgeDeploy ? styles.edgeDescriptor : {}]}>{descriptor}</Text>
            <View style={styles.propContainer}>
                <Prop
                    teamName={awayName}
                    propString={awayPropString}
                    index={index}
                    platformEventId={gameData.platformEvent}
                    platformOutcome={gameData.platformOutcomes[1]}
                    isOverUnder={isOverUnder}
                    isSelected={isAwaySelected}
                    roundLeft={true}
                    onSelect={onSelect}
                    disabled={disabled}
                />
                <Prop
                    teamName={homeName}
                    propString={homePropString}
                    index={index}
                    platformEventId={gameData.platformEvent}
                    platformOutcome={gameData.platformOutcomes[0]}
                    isOverUnder={isOverUnder}
                    isSelected={isHomeSelected}
                    roundLeft={false}
                    onSelect={onSelect}
                    disabled={disabled}
                />
            </View>
            <View style={[styles.atBox, edgeDeploy ? styles.edgeAtBox : {}]}>
                <Text style={[styles.atText]}>{circleText}</Text>
            </View>
        </View>
    );
};

const GenerateSelectionContainerStyles = () =>
    EStyleSheet.create({
        [MediaQueries.SMALL]: {
            edgeAtBox: {
                width: 54,
                height: 54,
                bottom: -5,
            },
            edgeDate: {
                fontSize: 14,
                lineHeight: 14,
                marginBottom: 8,
            },
            edgeMatch: {
                fontSize: 13,
                lineHeight: 13,
                marginBottom: 13,
            },
        },
        outerContainer: {
            width: "100%",
            alignItems: "center",
            alignContent: "center",
            justifyContent: "center",
            flexDirection: "column",
            marginBottom: 15,
        },
        noBottomMargin: {
            marginBottom: 0,
        },
        propContainer: {
            width: "100%",
            alignItems: "center",
            alignContent: "center",
            justifyContent: "center",
            flexDirection: "row",
        },
        atBox: {
            position: "absolute",
            backgroundColor: () => EStyleSheet.value(GlobalStyles.AT_BOX_BACKGROUND),
            width: 54,
            height: 54,
            bottom: -3,
            left: 0,
            right: 0,
            margin: "auto",
            borderRadius: "100%",
            borderColor: () => EStyleSheet.value(GlobalStyles.AT_BOX_BORDER),
            borderWidth: 2,
            justifyContent: "center",
            alignItems: "center",
        },
        edgeAtBox: {
            height: 80,
            width: 80,
            bottom: -10,
        },
        atText: {
            color: () => EStyleSheet.value(GlobalStyles.PROP_AT_TEXT),
            fontSize: 14,
            lineHeight: 14,
            fontWeight: "700",
            fontFamily: () => EStyleSheet.value(GlobalStyles.BASE_FONT),
        },
        descriptor: {
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
            fontFamily: () => EStyleSheet.value(GlobalStyles.BASE_FONT),
            fontSize: 12,
            fontWeight: "700",
            textAlign: "center",
            lineHeight: 12,
            marginBottom: 8,
        },
        edgeDescriptor: {
            marginBottom: 13,
            fontSize: 18,
            lineHeight: 18,
        },
        match: {
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
            fontFamily: () => EStyleSheet.value(GlobalStyles.BASE_FONT),
            fontSize: 13,
            fontWeight: "600",
            lineHeight: 18,
            textAlign: "center",
            marginBottom: 10,
        },
        edgeMatch: {
            fontSize: 14,
        },
    });
